import React, { useEffect, useState } from 'react';
import { Box, ButtonBase, CircularProgress, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { keyframes } from '@emotion/react';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import BuildCircleRoundedIcon from '@mui/icons-material/BuildCircleRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import { usePrefersReducedMotion } from './Reveal';

// A scripted, looping replay of the real assistant (same bubbles, tool chips and
// result cards as ChatPage / TasksPage). Nothing here talks to the backend.

const PRIORITY = {
  High: '#DC2626',
  Medium: '#D97706',
  Low: '#16A34A',
};

const popIn = keyframes`
  from { opacity: 0; transform: translateY(14px) scale(.96); }
  to   { opacity: 1; transform: none; }
`;
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: none; }
`;
const blink = keyframes`
  50% { opacity: 0; }
`;

const Chip: React.FC<{ color: string; icon?: React.ReactNode; children: React.ReactNode }> = ({
  color,
  icon,
  children,
}) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={0.4}
    sx={{
      px: 0.9,
      py: 0.2,
      borderRadius: 99,
      border: '1px solid',
      borderColor: alpha(color, 0.45),
      color,
      fontSize: 11.5,
      fontWeight: 600,
      whiteSpace: 'nowrap',
      '& svg': { fontSize: 13 },
    }}
  >
    {icon}
    <span>{children}</span>
  </Stack>
);

const TaskRow: React.FC<{ title: string; priority: keyof typeof PRIORITY; category?: string; due?: string }> = ({
  title,
  priority,
  category,
  due,
}) => (
  <Stack direction="row" spacing={1.25} alignItems="flex-start">
    <CheckBoxOutlineBlankRoundedIcon sx={{ fontSize: 20, color: '#9AA1B5', mt: '1px' }} />
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1F2333', lineHeight: 1.35 }}>{title}</Typography>
      <Stack direction="row" spacing={0.6} sx={{ mt: 0.6 }} flexWrap="wrap" useFlexGap>
        <Chip color={PRIORITY[priority]} icon={<FlagRoundedIcon />}>
          {priority}
        </Chip>
        {category && (
          <Chip color="#374151" icon={<FolderRoundedIcon />}>
            {category}
          </Chip>
        )}
        {due && (
          <Chip color="#374151" icon={<EventRoundedIcon />}>
            {due}
          </Chip>
        )}
      </Stack>
    </Box>
  </Stack>
);

interface Scenario {
  prompt: string;
  tools: string[];
  reply: string;
  toast: string;
  resultTitle: string;
  result: React.ReactNode;
}

const SCENARIOS: Scenario[] = [
  {
    prompt: 'Create a task to review the quarterly report, high priority, due Friday',
    tools: ['list categories tool', 'create todo tool'],
    reply: 'Done! "Review the quarterly report" is in your Work category — high priority, due Friday.',
    toast: 'Task created',
    resultTitle: 'Tasks',
    result: <TaskRow title="Review the quarterly report" priority="High" category="Work" due="Fri, Oct 9" />,
  },
  {
    prompt: 'Log 2 hours on API integration today',
    tools: ['list todos tool', 'create time log tool'],
    reply: 'Logged 2h on "API integration" for today. That makes 3h 15m so far — nice focus!',
    toast: 'Time logged',
    resultTitle: 'Time Logs · Today',
    result: (
      <Stack spacing={1.1}>
        {[
          ['2h', 'API integration', true],
          ['1h 15m', 'Frontend development', false],
        ].map(([d, t, fresh]) => (
          <Stack key={t as string} direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                minWidth: 58,
                textAlign: 'center',
                py: 0.4,
                borderRadius: 99,
                fontSize: 12.5,
                fontWeight: 700,
                color: '#fff',
                bgcolor: fresh ? '#4F46E5' : alpha('#4F46E5', 0.45),
              }}
            >
              {d}
            </Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1F2333' }}>{t}</Typography>
          </Stack>
        ))}
      </Stack>
    ),
  },
  {
    prompt: "What's due this week?",
    tools: ['list todos tool'],
    reply: 'Three things: send the client invoice (Wed), book the dentist (Thu) and review the quarterly report (Fri).',
    toast: '3 tasks found',
    resultTitle: 'Due this week',
    result: (
      <Stack spacing={1.4}>
        <TaskRow title="Send invoice to client" priority="Medium" due="Wed" />
        <TaskRow title="Book dentist appointment" priority="Low" due="Thu" />
        <TaskRow title="Review the quarterly report" priority="High" due="Fri" />
      </Stack>
    ),
  },
  {
    prompt: 'Make a Side project category and move my blog draft into it',
    tools: ['create category tool', 'list todos tool', 'update todo tool'],
    reply: 'Created "Side project" and moved "Write blog draft" into it. Want a due date on it too?',
    toast: 'Category created',
    resultTitle: 'Categories',
    result: (
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha('#0EA5E9', 0.12),
            color: '#0EA5E9',
          }}
        >
          <FolderRoundedIcon fontSize="small" />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1F2333' }}>Side project</Typography>
          <Typography sx={{ fontSize: 12.5, color: '#5A6072' }}>1 task · Write blog draft</Typography>
        </Box>
      </Stack>
    ),
  },
];

// Timeline of one scenario, in ms.
const TICK = 50;
const TYPE_START = 500;
const PER_CHAR = 34;
function timeline(s: Scenario) {
  const typed = TYPE_START + s.prompt.length * PER_CHAR;
  const sent = typed + 350;
  const toolAt = (i: number) => sent + 650 + i * 850;
  const toolDone = (i: number) => toolAt(i) + 600;
  const reply = toolAt(s.tools.length - 1) + 1000;
  const result = reply + 450;
  const end = result + 3600;
  return { typed, sent, toolAt, toolDone, reply, result, end };
}

const AssistantAvatar = () => (
  <Box
    sx={{
      width: 26,
      height: 26,
      borderRadius: '50%',
      flexShrink: 0,
      display: 'grid',
      placeItems: 'center',
      bgcolor: '#4F46E5',
      color: '#fff',
      '& svg': { fontSize: 15 },
    }}
  >
    <AutoAwesomeRoundedIcon />
  </Box>
);

const HeroDemo: React.FC = () => {
  const reduced = usePrefersReducedMotion();
  const [index, setIndex] = useState(0);
  const [t, setT] = useState(0);
  const scenario = SCENARIOS[index];
  const tl = timeline(scenario);
  // Reduced motion: show each finished exchange, no typing.
  const now = reduced ? tl.end - 1 : t;

  useEffect(() => {
    const id = window.setInterval(
      () => {
        if (document.hidden) return;
        if (reduced) {
          setIndex((i) => (i + 1) % SCENARIOS.length);
          return;
        }
        setT((prev) => prev + TICK);
      },
      reduced ? 7000 : TICK
    );
    return () => window.clearInterval(id);
  }, [reduced]);

  useEffect(() => {
    if (!reduced && t >= tl.end) {
      setIndex((i) => (i + 1) % SCENARIOS.length);
      setT(0);
    }
  }, [t, tl.end, reduced]);

  const jumpTo = (i: number) => {
    setIndex(i);
    setT(0);
  };

  const sent = now >= tl.sent;
  const typedChars = sent ? 0 : Math.max(0, Math.floor((now - TYPE_START) / PER_CHAR));
  const draft = scenario.prompt.slice(0, typedChars);
  const showResult = now >= tl.result;

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        role="img"
        aria-label="Animated demo: you type a request such as “Create a task to review the quarterly report, high priority, due Friday”, the TaskFlow assistant calls its tools, and the new task appears in your list."
        sx={{ position: 'relative' }}
      >
        <Box aria-hidden>
          {/* Browser window */}
          <Box
            sx={{
              position: 'relative',
              borderRadius: 4,
              overflow: 'hidden',
              bgcolor: '#F4F5FA',
              border: '1px solid rgba(255,255,255,0.14)',
              boxShadow: '0 40px 80px -24px rgba(0,0,0,0.65), 0 0 0 8px rgba(255,255,255,0.04)',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ px: 2, py: 1.25, bgcolor: '#FFFFFF', borderBottom: '1px solid #E5E7F0' }}
            >
              <Stack direction="row" spacing={0.75}>
                {['#FF5F57', '#FEBC2E', '#28C840'].map((c) => (
                  <Box key={c} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c }} />
                ))}
              </Stack>
              <Box
                sx={{
                  flex: 1,
                  maxWidth: 260,
                  mx: 'auto !important',
                  py: 0.35,
                  borderRadius: 99,
                  bgcolor: '#F1F2F7',
                  color: '#5A6072',
                  fontSize: 12,
                  textAlign: 'center',
                }}
              >
                taskflowai.in/assistant
              </Box>
              <Box sx={{ width: 46 }} />
            </Stack>

            {/* Toast */}
            {showResult && (
              <Stack
                key={`toast-${index}`}
                direction="row"
                spacing={0.75}
                alignItems="center"
                sx={{
                  position: 'absolute',
                  top: 56,
                  right: 14,
                  zIndex: 2,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 2,
                  bgcolor: '#16A34A',
                  color: '#fff',
                  fontSize: 12.5,
                  fontWeight: 600,
                  boxShadow: '0 8px 20px -6px rgba(22,163,74,0.6)',
                  animation: reduced ? 'none' : `${popIn} .4s ease both`,
                  '& svg': { fontSize: 16 },
                }}
              >
                <CheckCircleRoundedIcon />
                <span>{scenario.toast}</span>
              </Stack>
            )}

            {/* Conversation */}
            <Stack
              justifyContent="flex-end"
              spacing={1.25}
              sx={{ height: { xs: 330, sm: 350 }, px: { xs: 1.75, sm: 2.5 }, pt: 2, pb: 1.5, overflow: 'hidden' }}
            >
              {!sent && (
                <Stack alignItems="center" spacing={1} sx={{ flex: 1, justifyContent: 'center', color: '#9AA1B5' }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '16px',
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: alpha('#4F46E5', 0.1),
                      color: '#4F46E5',
                    }}
                  >
                    <AutoAwesomeRoundedIcon />
                  </Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#1F2333' }}>How can I help today?</Typography>
                  <Typography sx={{ fontSize: 12.5 }}>Tasks, time logs, categories — just ask.</Typography>
                </Stack>
              )}

              {sent && (
                <Box
                  key={`user-${index}`}
                  sx={{
                    alignSelf: 'flex-end',
                    display: 'flex',
                    gap: 1,
                    alignItems: 'flex-end',
                    maxWidth: '88%',
                    animation: reduced ? 'none' : `${fadeUp} .35s ease both`,
                  }}
                >
                  <Box
                    sx={{
                      px: 1.75,
                      py: 1,
                      borderRadius: '16px 16px 4px 16px',
                      bgcolor: '#4F46E5',
                      color: '#fff',
                      fontSize: 13.5,
                      lineHeight: 1.45,
                    }}
                  >
                    {scenario.prompt}
                  </Box>
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: '50%',
                      flexShrink: 0,
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: '#D7DAE5',
                      color: '#fff',
                      '& svg': { fontSize: 17 },
                    }}
                  >
                    <PersonRoundedIcon />
                  </Box>
                </Box>
              )}

              {scenario.tools.map((tool, i) =>
                now >= tl.toolAt(i) ? (
                  <Stack
                    key={`${index}-${tool}`}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ animation: reduced ? 'none' : `${fadeUp} .3s ease both` }}
                  >
                    <AssistantAvatar />
                    <Stack
                      direction="row"
                      spacing={0.6}
                      alignItems="center"
                      sx={{
                        px: 1,
                        py: 0.3,
                        borderRadius: 99,
                        border: '1px solid',
                        borderColor: alpha('#0EA5E9', 0.6),
                        bgcolor: alpha('#0EA5E9', 0.06),
                        color: '#0284C7',
                        fontSize: 12.5,
                        fontWeight: 600,
                      }}
                    >
                      {now >= tl.toolDone(i) ? (
                        <BuildCircleRoundedIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <CircularProgress size={12} thickness={6} sx={{ color: '#0EA5E9', m: '2px' }} />
                      )}
                      <span>{tool}</span>
                    </Stack>
                  </Stack>
                ) : null
              )}

              {sent && now < tl.toolAt(0) && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <AssistantAvatar />
                  <Typography sx={{ fontSize: 12.5, color: '#5A6072', fontStyle: 'italic' }}>Thinking…</Typography>
                </Stack>
              )}

              {now >= tl.reply && (
                <Stack
                  key={`reply-${index}`}
                  direction="row"
                  spacing={1}
                  alignItems="flex-end"
                  sx={{ maxWidth: '92%', animation: reduced ? 'none' : `${fadeUp} .35s ease both` }}
                >
                  <AssistantAvatar />
                  <Box
                    sx={{
                      px: 1.75,
                      py: 1,
                      borderRadius: '16px 16px 16px 4px',
                      bgcolor: '#fff',
                      border: '1px solid #E5E7F0',
                      color: '#1F2333',
                      fontSize: 13.5,
                      lineHeight: 1.5,
                    }}
                  >
                    {scenario.reply}
                  </Box>
                </Stack>
              )}
            </Stack>

            {/* Composer */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ px: { xs: 1.75, sm: 2.5 }, pb: 2 }}>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  px: 2,
                  py: 1.1,
                  borderRadius: 99,
                  bgcolor: '#fff',
                  border: '1px solid',
                  borderColor: draft ? '#4F46E5' : '#E5E7F0',
                  fontSize: 13.5,
                  color: draft ? '#1F2333' : '#9AA1B5',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  direction: 'rtl',
                  textAlign: 'left',
                  transition: 'border-color .2s',
                }}
              >
                <bdi>
                  {draft || 'Message the assistant…'}
                  {draft && (
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        width: '2px',
                        height: '1em',
                        ml: '1px',
                        verticalAlign: '-2px',
                        bgcolor: '#4F46E5',
                        animation: `${blink} 1s step-end infinite`,
                      }}
                    />
                  )}
                </bdi>
              </Box>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: draft ? '#4F46E5' : '#E5E7F0',
                  color: '#fff',
                  transition: 'background-color .2s',
                  '& svg': { fontSize: 18 },
                }}
              >
                <SendRoundedIcon />
              </Box>
            </Stack>
          </Box>

          {/* Floating result card: what the assistant changed in your workspace */}
          {showResult && (
            <Box
              key={`result-${index}`}
              sx={{
                position: { xs: 'relative', md: 'absolute' },
                left: { md: -56 },
                bottom: { md: -64 },
                mt: { xs: 2, md: 0 },
                width: { xs: '100%', md: 320 },
                zIndex: 3,
                p: 2,
                borderRadius: 3,
                bgcolor: '#fff',
                border: '1px solid #E5E7F0',
                boxShadow: '0 24px 48px -16px rgba(15,18,40,0.55)',
                animation: reduced ? 'none' : `${popIn} .5s cubic-bezier(.2,.8,.2,1.2) both`,
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  color: '#4F46E5',
                  mb: 1.25,
                }}
              >
                {scenario.resultTitle} · just now
              </Typography>
              {scenario.result}
            </Box>
          )}
        </Box>
      </Box>

      {/* Scenario picker */}
      <Stack
        direction="row"
        spacing={1}
        justifyContent={{ xs: 'center', md: 'flex-end' }}
        sx={{ mt: { xs: 2.5, md: 3 } }}
      >
        {SCENARIOS.map((s, i) => (
          <ButtonBase
            key={s.prompt}
            onClick={() => jumpTo(i)}
            aria-label={`Show demo ${i + 1}: ${s.prompt}`}
            aria-current={i === index}
            sx={{
              width: i === index ? 28 : 10,
              height: 10,
              borderRadius: 99,
              bgcolor: i === index ? '#A5B4FC' : 'rgba(255,255,255,0.25)',
              transition: 'all .3s',
              '&:hover': { bgcolor: i === index ? '#A5B4FC' : 'rgba(255,255,255,0.45)' },
              '&:focus-visible': { outline: '2px solid #A5B4FC', outlineOffset: 3 },
            }}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default HeroDemo;
